// Rejestracja Service Workera + auto-aktualizacja PWA bez udziału użytkownika.
//
// Mechanizm:
// 1. sw.js ma numer wersji wstrzyknięty przy buildzie (patrz scripts/prebuild.js),
//    więc realnie zmienia bajty przy każdym deployu — przeglądarka wykrywa nowy SW.
// 2. Sprawdzenie aktualizacji odbywa się: raz przy starcie aplikacji oraz przy
//    każdym powrocie połączenia (wywoływane z useNetworkSync) — celowo BEZ
//    cyklicznego pollingu w tle.
// 3. Gdy nowy SW się aktywuje, strona przeładowuje się automatycznie — ale
//    NIGDY w trakcie aktywnego dyktowania/przetwarzania notatki (patrz
//    setDictationActive) — w takim wypadku reload jest odkładany.

let registration: ServiceWorkerRegistration | null = null
let dictationActive = false
let reloadPending = false

const RELOAD_RETRY_MS = 2000
const RELOAD_MAX_RETRIES = 30 // ok. 1 minuta odkładania, potem czeka do następnej okazji

// Słuchacze końca dyktowania — używane przez useNetworkSync do wyzwolenia
// synchronizacji natychmiast po zakończeniu dyktowania (patrz isDictationActive
// niżej: synchronizacja nigdy nie startuje W TRAKCIE dyktowania, więc bez tego
// musiałaby czekać na następny cykl/zdarzenie 'online').
const dictationEndListeners: (() => void)[] = []
export function onDictationEnd(cb: () => void): void {
  dictationEndListeners.push(cb)
}

export function isDictationActive(): boolean {
  return dictationActive
}

export function setDictationActive(active: boolean): void {
  dictationActive = active
  if (!active) {
    if (reloadPending) {
      reloadPending = false
      window.location.reload()
      return
    }
    dictationEndListeners.forEach(cb => { try { cb() } catch (_) {} })
  }
}

function reloadWhenSafe(retries = RELOAD_MAX_RETRIES): void {
  if (!dictationActive) {
    window.location.reload()
    return
  }
  reloadPending = true
  if (retries <= 0) return // spróbujemy ponownie po zakończeniu dyktowania (setDictationActive(false))
  setTimeout(() => reloadWhenSafe(retries - 1), RELOAD_RETRY_MS)
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return

  navigator.serviceWorker
    .register('/sw.js', { updateViaCache: 'none' })
    .then((reg) => {
      registration = reg
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing
        if (!newWorker) return
        const onStateChange = () => {
          if (newWorker.state === 'activated') {
            reloadWhenSafe()
          }
        }
        onStateChange()
        newWorker.addEventListener('statechange', onStateChange)
      })
    })
    .catch((err) => console.warn('[SW] Błąd rejestracji service workera:', err))

  // Telefon/PWA bardzo często NIE robi pełnego przeładowania po "otwarciu aplikacji" —
  // system tylko wznawia zawieszoną w tle kartę (bez ponownego wykonania JS-a).
  // Dlatego dodatkowo sprawdzamy aktualizację przy każdym powrocie na pierwszy plan
  // (zdarzeniowo, NIE cyklicznie w tle).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      checkForUpdate()
    }
  })
}

// Wywoływane z useNetworkSync przy powrocie połączenia — bez cyklicznego pollingu.
export function checkForUpdate(): void {
  registration?.update().catch(() => {})
}
